import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { studioDayKey, studioDayWithTimeOf, studioLocalIso } from '@atink/domain';
import { DEMO_NOW } from '@/domain/demoClock';
import { useBrainStore } from '@/state/brainStore';

export type SimSpeed = 0 | 1 | 4 | 16 | 30;

export interface FiredEventRecord {
  eventId: string;
  firedAt: string; // ISO real-world time
  simTime: string; // ISO sim time
}

// The clock boots at DEMO_NOW. The facilitator can move it (the header day
// stepper, the console's +24h cohort trigger); nothing moves it on its own.
const DEFAULT_START = DEMO_NOW;

// A clock the facilitator moved is kept in localStorage (tv-2na.5), so it
// survives a reload / persona switch and every same-origin tab (the /console
// remote and the app tab) agrees on "now". At DEMO_NOW the key is removed, so
// "reset" is the absence of the key and a fresh browser always boots on the
// seed day.
export const SIM_CLOCK_STORAGE_KEY = 'ati-sim-clock';

const isValidIso = (v: string | null): v is string => !!v && !Number.isNaN(Date.parse(v));

function readStoredClock(): string | null {
  try {
    const v = localStorage.getItem(SIM_CLOCK_STORAGE_KEY);
    return isValidIso(v) ? v : null;
  } catch {
    return null; // storage unavailable — boot at DEMO_NOW.
  }
}

function writeStoredClock(iso: string): void {
  try {
    if (Date.parse(iso) === Date.parse(DEFAULT_START)) localStorage.removeItem(SIM_CLOCK_STORAGE_KEY);
    else localStorage.setItem(SIM_CLOCK_STORAGE_KEY, iso);
  } catch {
    // storage unavailable — the in-memory clock still applies to this tab.
  }
}

export interface SimulationState {
  clockIso: string;
  speed: SimSpeed;
  running: boolean;
  startedAt: string | null;
  firedEvents: FiredEventRecord[];
  scenarioCursor: number | null;
  setClock: (iso: string) => void;
  stepDay: (delta: number) => void;
  resetClock: () => void;
  setSpeed: (speed: SimSpeed) => void;
  setRunning: (running: boolean) => void;
  reset: () => void;
  recordFired: (eventId: string) => void;
  setScenarioCursor: (idx: number | null) => void;
}

export const useSimulationStore = create<SimulationState>()(
  persist(
    (set) => ({
      clockIso: readStoredClock() ?? DEFAULT_START,
      speed: 1,
      running: false,
      startedAt: null,
      firedEvents: [],
      scenarioCursor: null,
      setClock: (iso) => set({ clockIso: iso }),
      // Facilitator day-stepper. A PURE view change: it only moves clockIso by
      // whole days (offset-preserving, so the time-of-day is untouched) and then
      // re-derives today's stats for the new day. It never writes a persistable
      // entity, never calls handleSimEvent, and never fires a scripted beat.
      // The clock itself is kept in localStorage by the subscriber below.
      stepDay: (delta) => {
        // Emit the canonical studio-local form (Melbourne wall time with its real
        // offset, +10:00 or +11:00 in daylight saving; 7fra), never a UTC Z
        // string: readers slice the clock string for the hour / day key, and a Z
        // string would read 03:00 for 13:00. The Melbourne time of day is kept.
        set((s) => {
          const [y, m, d] = studioDayKey(s.clockIso).split('-').map(Number);
          const day = new Date(Date.UTC(y!, m! - 1, d! + delta)).toISOString().slice(0, 10);
          return { clockIso: studioLocalIso(Date.parse(studioDayWithTimeOf(s.clockIso, day))) };
        });
        useBrainStore.getState().recomputeTodayStats();
      },
      resetClock: () => {
        set({ clockIso: DEFAULT_START });
        useBrainStore.getState().recomputeTodayStats();
      },
      setSpeed: (speed) => set({ speed }),
      setRunning: (running) =>
        set({ running, startedAt: running ? new Date().toISOString() : null }),
      reset: () =>
        set({
          clockIso: DEFAULT_START,
          speed: 1,
          running: false,
          startedAt: null,
          firedEvents: [],
          scenarioCursor: null,
        }),
      recordFired: (eventId) =>
        set((s) => ({
          firedEvents: [
            ...s.firedEvents,
            { eventId, firedAt: new Date().toISOString(), simTime: s.clockIso },
          ],
        })),
      setScenarioCursor: (idx) => set({ scenarioCursor: idx }),
    }),
    {
      name: 'ati-simulation',
      storage: createJSONStorage(() => sessionStorage),
      // clockIso is NOT in this per-tab sessionStorage blob: it lives under
      // SIM_CLOCK_STORAGE_KEY in localStorage so every tab shares one clock.
      partialize: (s) => ({
        speed: s.speed,
        firedEvents: s.firedEvents,
        scenarioCursor: s.scenarioCursor,
      }),
    },
  ),
);

// Mirror every clock move to localStorage (the other tabs pick it up below).
useSimulationStore.subscribe((s, prev) => {
  if (s.clockIso !== prev.clockIso) writeStoredClock(s.clockIso);
});

// Follow a clock moved (or reset) in another same-origin tab. The browser fires
// `storage` only in the OTHER tabs, so this never echoes back to the writer.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (e) => {
    // key === null means the whole storage was cleared: back to DEMO_NOW.
    if (e.key !== null && e.key !== SIM_CLOCK_STORAGE_KEY) return;
    const next = isValidIso(e.newValue) && e.key !== null ? e.newValue : DEFAULT_START;
    if (next === useSimulationStore.getState().clockIso) return;
    useSimulationStore.setState({ clockIso: next });
    try {
      useBrainStore.getState().recomputeTodayStats();
    } catch {
      // brain not hydrated yet — its load computes against the new clock.
    }
  });
}

export const SIM_START_ISO = DEFAULT_START;
